import React, { createContext, useCallback, useEffect, useState } from "react";
import api from "../utils/axios";
import { API_ROUTES } from "../apis/routes";

export const AuthContext = createContext();

// The session lives in an httpOnly cookie, so the browser can't read it —
// "am I signed in?" is answered by asking the server, not by looking in
// localStorage. `role` is whatever /me reports, which is read fresh from the
// Admin record on every request rather than baked into a token at login.
//
// The exported shape ({ isAuthenticated, role, loading, login, logout }) is
// unchanged from the localStorage version, so NavBar, ProtectedRoute,
// AuthRedirect and the api hooks that consume it need no edits.
const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [role, setRole] = useState(null);
  const [user, setUser] = useState(null);
  const [mfaEnrolled, setMfaEnrolled] = useState(true);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get(API_ROUTES.ME);
      const me = data?.data || null;
      setUser(me);
      setRole(me?.role ?? null);
      setIsAuthenticated(Boolean(me));
      // Read enrolment from the payload, NOT from the fact that the call
      // succeeded. /me is deliberately on the backend's MFA-exempt list, so an
      // un-enrolled admin gets a perfectly good 200 here — assuming success
      // meant "enrolled" sent them to the dashboard, whose first API call then
      // 403'd them back to /mfa-setup, which bounced them to the dashboard
      // again.
      // mfaSkipped: the backend's local-dev 2FA skip (ADMIN_SKIP_MFA) is on.
      setMfaEnrolled(Boolean(me?.twoFactorEnabled || me?.mfaSkipped));
      return me;
    } catch (error) {
      // 403 MFA_REQUIRED means the session is real but enrolment is unfinished.
      // That is still "signed in" — the enrolment screen needs a session to
      // work — so the flag is separate from isAuthenticated.
      if (error?.response?.data?.code === "MFA_REQUIRED") {
        setIsAuthenticated(true);
        setMfaEnrolled(false);
        return null;
      }
      setUser(null);
      setRole(null);
      setIsAuthenticated(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Called after a completed sign-in. The cookie is already set by then; this
  // just pulls the profile so the nav can render without a page reload.
  const login = useCallback(async () => {
    setLoading(true);
    return refresh();
  }, [refresh]);

  // Local state only. For callers whose own request already ended the session
  // server-side (change-password, sign-out-everywhere) — calling logout() there
  // would fire a second, pointless /logout.
  const clearSession = useCallback(() => {
    setUser(null);
    setRole(null);
    setIsAuthenticated(false);
    setMfaEnrolled(true);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.get(API_ROUTES.LOGOUT);
    } catch {
      // Clearing local state matters more than the round trip succeeding.
    }
    clearSession();
  }, [clearSession]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        loading,
        login,
        logout,
        clearSession,
        role,
        user,
        mfaEnrolled,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
